import React, { useState, useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { Check, ArrowUpRight, Expand } from "lucide-react";
import { COURTS, COURT_LINKS, BOOKING_URL } from "../../mock/mock";
import { fetchCourtsPublic, resolveImg } from "../adminApi";
import D2Banner from "../D2Banner";
import { Reveal, Counter, SplitWords } from "../motionHelpers";
import Lightbox from "../Lightbox";

export default function CourtPage() {
  const { slug } = useParams();
  const [lightbox, setLightbox] = useState(null);
  const [courtsMap, setCourtsMap] = useState(null);

  useEffect(() => {
    fetchCourtsPublic()
      .then((list) => setCourtsMap(Object.fromEntries(list.map((c) => [c.slug, c]))))
      .catch(() => setCourtsMap({}));
  }, []);

  const data = (courtsMap && courtsMap[slug]) || COURTS[slug];
  if (!data) return <Navigate to="/courts/pickleball" replace />;

  const getCourt = (s) => (courtsMap && courtsMap[s]) || COURTS[s] || {};
  const galleryImgs = (data.gallery || []).map(resolveImg);
  const others = COURT_LINKS.filter((c) => c.slug !== slug);

  return (
    <div>
      <D2Banner eyebrow={data.eyebrow} title={data.title} subtitle={data.tagline} image={resolveImg(data.hero)}
        crumbs={[{ label: "The Courts", to: "/courts/pickleball" }, { label: data.title }]} />

      <section className="d2-bg py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <button type="button" onClick={() => setLightbox(0)} data-hover data-testid="court-hero-image"
              className="group relative block w-full rounded-2xl overflow-hidden shadow-2xl">
              <img src={galleryImgs[0]} alt={data.title} loading="lazy"
                className="w-full h-[440px] object-cover transition-transform duration-700 group-hover:scale-105" />
              <span className="absolute inset-0 bg-[#06170f]/0 group-hover:bg-[#06170f]/20 transition-colors" />
              <span className="absolute bottom-4 right-4 w-11 h-11 rounded-full bg-white/15 backdrop-blur text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Expand className="w-5 h-5" />
              </span>
            </button>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display uppercase text-white text-4xl md:text-6xl leading-[0.92]">
              <SplitWords text={data.title} />
            </h2>
            <p className="text-white/70 text-lg mt-6 font-grotesk">{data.body}</p>
            <ul className="grid sm:grid-cols-2 gap-3 mt-8">
              {data.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-white/80 font-grotesk">
                  <Check className="w-5 h-5 text-sc-lime mt-0.5 shrink-0" />{f}
                </li>
              ))}
            </ul>
            <a href={BOOKING_URL} className="d2-btn inline-flex items-center gap-3 px-8 py-4 mt-8 text-sm">Book Now! <ArrowUpRight className="w-4 h-4" /></a>
          </Reveal>
        </div>

        {/* stats */}
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 mt-16">
          <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden">
            {data.stats.map((s) => (
              <div key={s.label} className="bg-[#06170f] p-8 text-center">
                <div className="font-display text-5xl md:text-6xl text-sc-lime">{s.value}</div>
                <div className="font-grotesk uppercase tracking-widest text-xs text-white/50 mt-2">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* gallery */}
      <section className="bg-sc-cream py-24">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <h2 className="font-display uppercase text-sc-green text-4xl md:text-6xl">Gallery</h2>
            <p className="font-grotesk text-sc-green/60 text-sm max-w-xs">Tap any photo to explore the real Supreme Courts facility up close.</p>
          </div>
          <div className="[column-fill:_balance] columns-1 sm:columns-2 lg:columns-3 gap-4 md:gap-5 [&>*]:mb-4 md:[&>*]:mb-5">
            {galleryImgs.map((src, i) => (
              <Reveal key={src} delay={(i % 3) * 0.07}>
                <button
                  type="button"
                  onClick={() => setLightbox(i)}
                  data-hover
                  data-testid={`gallery-item-${i}`}
                  className="group relative block w-full overflow-hidden rounded-xl break-inside-avoid focus:outline-none focus:ring-2 focus:ring-sc-lime"
                >
                  <img
                    src={src}
                    alt={`${data.title} — photo ${i + 1}`}
                    loading="lazy"
                    className="w-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                  />
                  <span className="absolute inset-0 bg-gradient-to-t from-[#06170f]/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="absolute bottom-3 right-3 w-10 h-10 rounded-full bg-sc-lime text-[#06170f] flex items-center justify-center translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                    <Expand className="w-4 h-4" />
                  </span>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* explore others */}
      <section className="d2-bg py-20">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8">
          <h3 className="font-grotesk uppercase tracking-[0.3em] text-sc-lime text-sm mb-6">Explore More</h3>
          <div className="grid sm:grid-cols-3 gap-5">
            {others.map((o) => (
              <Link key={o.slug} to={`/courts/${o.slug}`} data-hover className="group relative h-48 rounded-2xl overflow-hidden">
                <img src={resolveImg(getCourt(o.slug).hero)} alt={o.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#06170f] to-transparent" />
                <div className="absolute bottom-0 left-0 p-5 flex items-center justify-between w-full">
                  <span className="font-display uppercase text-2xl text-white">{o.title}</span>
                  <ArrowUpRight className="w-5 h-5 text-sc-lime group-hover:rotate-45 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Lightbox
        images={galleryImgs}
        index={lightbox}
        onClose={() => setLightbox(null)}
        onChange={setLightbox}
        alt={data.title}
      />
    </div>
  );
}
